import { useInfiniteQuery, useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  leaveApi,
  leaveBalanceApi,
  type ApplyLeavePayload,
  type ReviewLeavePayload,
  type LeaveStatus,
} from "../lib/leaveApi";

export const LEAVE_KEYS = {
  all: ["leaves"] as const,
  mine: (status?: LeaveStatus) => [...LEAVE_KEYS.all, "mine", status ?? "ALL"] as const,
  adminList: (businessId: string, status: string) =>
    [...LEAVE_KEYS.all, "admin", businessId, status] as const,
};

// staleTime: 0 — leave data changes on every approval/rejection so treat it
// as always stale; refetch on every mount and window focus.
const LEAVE_QUERY_OPTIONS = {
  staleTime: 0,
  refetchOnMount: true,
  refetchOnWindowFocus: true,
} as const;

export function useMyLeaves(status?: LeaveStatus) {
  return useInfiniteQuery({
    queryKey: LEAVE_KEYS.mine(status),
    queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
      leaveApi.listMine(status, { limit: 10, cursor: pageParam }),
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    initialPageParam: undefined as string | undefined,
    ...LEAVE_QUERY_OPTIONS,
  });
}

export function useAdminLeaves(businessId: string, status = "PENDING", enabled = true) {
  return useQuery({
    queryKey: LEAVE_KEYS.adminList(businessId, status),
    queryFn: () => leaveApi.adminList(businessId, status),
    enabled: enabled && !!businessId,
    ...LEAVE_QUERY_OPTIONS,
  });
}

export function useApplyLeave() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ApplyLeavePayload) => leaveApi.apply(payload),
    // Await invalidation so mutateAsync only resolves after the fresh data is fetched
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: LEAVE_KEYS.all });
    },
  });
}

export function useApproveLeave() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ leaveId, payload }: { leaveId: string; payload: ReviewLeavePayload }) =>
      leaveApi.approve(leaveId, payload),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: LEAVE_KEYS.all });
    },
  });
}

export function useRejectLeave() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ leaveId, payload }: { leaveId: string; payload: ReviewLeavePayload }) =>
      leaveApi.reject(leaveId, payload),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: LEAVE_KEYS.all });
    },
  });
}

// ── Leave Balance hooks ───────────────────────────────────────────────────────

export const LEAVE_BALANCE_KEYS = {
  all: ["leaveBalances"] as const,
  policy: (businessId: string) => [...LEAVE_BALANCE_KEYS.all, "policy", businessId] as const,
  balances: (businessId: string, year: number) =>
    [...LEAVE_BALANCE_KEYS.all, "list", businessId, year] as const,
  myBalance: (businessId: string) =>
    [...LEAVE_BALANCE_KEYS.all, "mine", businessId] as const,
};

export function useLeavePolicy(businessId: string | null | undefined) {
  return useQuery({
    queryKey: LEAVE_BALANCE_KEYS.policy(businessId ?? ""),
    queryFn: () => leaveBalanceApi.getPolicy(businessId!),
    enabled: !!businessId,
    staleTime: 30_000,
  });
}

export function useSaveLeavePolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ businessId, creditsPerYear }: { businessId: string; creditsPerYear: number }) =>
      leaveBalanceApi.savePolicy(businessId, creditsPerYear),
    onSuccess: async (_data, vars) => {
      await qc.invalidateQueries({ queryKey: LEAVE_BALANCE_KEYS.policy(vars.businessId) });
    },
  });
}

export function useLeaveBalances(businessId: string | null | undefined, year: number) {
  return useQuery({
    queryKey: LEAVE_BALANCE_KEYS.balances(businessId ?? "", year),
    queryFn: () => leaveBalanceApi.listBalances(businessId!, year),
    enabled: !!businessId,
    staleTime: 0,
  });
}

export function useSetLeaveBalance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      businessId,
      userId,
      payload,
    }: {
      businessId: string;
      userId: string;
      payload: { year: number; totalCredits: number; usedCredits: number; employeeName: string };
    }) => leaveBalanceApi.setBalance(businessId, userId, payload),
    onSuccess: async (_data, vars) => {
      await qc.invalidateQueries({
        queryKey: LEAVE_BALANCE_KEYS.balances(vars.businessId, vars.payload.year),
      });
    },
  });
}

export function useAdjustLeaveBalance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      businessId,
      userId,
      payload,
    }: {
      businessId: string;
      userId: string;
      payload: { delta: number; reason: string; year?: number };
    }) => leaveBalanceApi.adjustBalance(businessId, userId, payload),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: LEAVE_BALANCE_KEYS.all });
    },
  });
}

export function useBulkInitLeaveBalances() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ businessId, year }: { businessId: string; year: number }) =>
      leaveBalanceApi.bulkInit(businessId, year),
    onSuccess: async (_data, vars) => {
      await qc.invalidateQueries({
        queryKey: LEAVE_BALANCE_KEYS.balances(vars.businessId, vars.year),
      });
    },
  });
}

export function useMyLeaveBalance(businessId: string | null | undefined) {
  return useQuery({
    queryKey: LEAVE_BALANCE_KEYS.myBalance(businessId ?? ""),
    queryFn: () => leaveBalanceApi.getMyBalance(businessId!),
    enabled: !!businessId,
    staleTime: 30_000,
    refetchInterval: 30_000,
  });
}
